import { rem } from 'polished';
import React, { useState, useRef, useContext } from 'react';
import styled from 'styled-components';

import { FatalAttraction } from '@vpr/tracking';

import { translate } from '@vimeo/i18n-runtime';
import { PopOver, Pop, Button } from '@vimeo/iris/components';
import { Text } from '@vimeo/iris/typography';
import { Override, IrisInputProps, Attach, AttachAlias } from '@vimeo/iris/utils';
import { ViewerContext } from '@vimeo/viewer';

import useOnClickOutside from '../../../hooks/use-outside-click/useOnClickOutside';
import DoNotSellLink from '../DoNotSellLink';
import { getLegalLinks } from '../Links';
import { PopLink } from './PopLink';

const Container = styled.div`
  display: none;
  @media screen and (min-width: ${rem(480)}) {
    display: flex;
    align-items: center;
  }
`;

const Content = styled.div`
  padding: ${rem(6)} 0;
`;

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.elements.color1};
`;

type A11yStyledButtonType = Override<
  IrisInputProps,
  {
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  }
>;

const StyledButton = styled(Button)<A11yStyledButtonType>`
  span {
    font-size: ${rem(12)};
    transition: 170ms color ease-in-out;
  }

  &:hover {
    span {
      color: ${({ theme }) => theme.elements.color2};
    }
  }
`;

const StyledPopOver = styled(PopOver)`
  border-radius: ${rem(8)};
`;

const TerminateLink = styled.a`
  text-decoration: none;
  &:hover {
    ${StyledText} {
      color: ${({ theme }) => theme.elements.color2};
    }
  }
  ${StyledText} {
    font-size: ${rem(12)};
    letter-spacing: ${rem(1)};
    font-weight: bold;
    line-height: 1.25;
  }
`;

interface Props {
  title?: string;
  impressumQualifies?: boolean;
  terminateContractQualifies?: boolean;
  isDoNotSellReady?: boolean;
  attach?: Attach | AttachAlias | undefined;
  onOpenLegalMenu?: () => void;
  className?: string;
}

export const EssentialLegalMenu = ({
  title = translate('Terms & Privacy'),
  impressumQualifies,
  terminateContractQualifies,
  isDoNotSellReady,
  attach = 'top',
  onOpenLegalMenu,
  className,
}: Props) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const viewer = useContext(ViewerContext);
  const isSimplifiedSite = viewer?.isSimplifiedSite;

  const legalLinks = getLegalLinks().filter(
    (link) =>
      (impressumQualifies ? true : link.title !== 'Impressum') &&
      (!isSimplifiedSite || link.forSimplifiedSite === isSimplifiedSite),
  );

  const toggleMenu = (open: boolean) => {
    setMenuOpen(open);
    if (open) {
      onOpenLegalMenu?.();
    }
  };

  useOnClickOutside([contentRef, buttonRef], () => toggleMenu(false), null, [menuOpen]);

  return (
    <Container>
      {terminateContractQualifies ? (
        <TerminateLink
          href="/settings/billing/membership_plan/optout"
          role="link"
          onClick={() => {
            FatalAttraction.trackClick({
              container: 'footer',
              component: 'logged_in',
              keyword: 'terminate_here',
              target: '/settings/billing/membership_plan/optout',
            });
          }}
        >
          <StyledText>{translate('Terminate contracts here')}</StyledText>
        </TerminateLink>
      ) : null}
      <StyledPopOver
        active={menuOpen}
        attach={attach}
        content={
          <Content ref={contentRef}>
            <Pop.List role="menu">
              {legalLinks.map((link) => (
                <PopLink
                  role="menuitem"
                  onClick={() => toggleMenu(false)}
                  key={link.label}
                  {...link}
                />
              ))}
              <DoNotSellLink footerType="essential" isDoNotSellReady={isDoNotSellReady} />
            </Pop.List>
          </Content>
        }
      >
        <div ref={buttonRef}>
          <StyledButton
            id="essential-footer-legal-menu-button"
            onClick={() => toggleMenu(!menuOpen)}
            title={title}
            format="secondary"
            variant="hyperminimal"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.code === 'Enter' || event.code === 'Space') {
                () => toggleMenu(!menuOpen);
              }
            }}
            className={className}
          >
            <StyledText>{title}</StyledText>
          </StyledButton>
        </div>
      </StyledPopOver>
    </Container>
  );
};
